import { useContext } from 'react';
import { Menu, useMenuContext } from '@shared/components/Menu';
import { MBSelectAttachmentType } from '@shared/scenes/upload/util/types';
import { grid } from '@shared/styles';
import { MBAnalytics } from '@shared/util/analytics';
import { i18n } from '@shared/util/i18n';
import { ChannelPermissionsContext } from '@shared/util/permissions/ChannelPermissionsContext';
import { Can } from '@shared/util/permissions/hooks';
import styles from './PlusButton2.module.css';

interface PlusButtonProps {
  draftId: string;
  urgent: boolean;
  hideUrgent?: boolean;
  onCreateCard: () => void;
  onShowGIFModal: () => void;
  onSelectAttachment: (type: MBSelectAttachmentType) => void;
  onSendAsUrgent: () => void;
  onRemoveSendAsUrgent: () => void;
}

export function PlusButton({
  draftId,
  urgent,
  hideUrgent,
  onCreateCard,
  onShowGIFModal,
  onSelectAttachment,
  onSendAsUrgent,
  onRemoveSendAsUrgent,
}: PlusButtonProps) {
  const channelPermissions = useContext(ChannelPermissionsContext);
  const channelAbility = channelPermissions.channelAbility;

  const handleSendAsUrgent = () => {
    // Note(fant): on web the urgent info modal will be
    // auto closed if this happens sync. If we show it
    // in the next tick, react-modal won't close immediately
    setTimeout(onSendAsUrgent);
    MBAnalytics.track('Chat::Input::PlusButton::SendAsUrgent', { draftId });
  };

  const onClickCreate = () => {
    onCreateCard();
    MBAnalytics.track('Chat::Input::PlusButton::CreateCard', { draftId });
  };

  const handleRemoveSendAsUrgent = () => {
    onRemoveSendAsUrgent();
    MBAnalytics.track('Chat::Input::PlusButton::RemoveSendAsUrgent', {
      draftId,
    });
  };

  const handleShowGIFModal = () => {
    onShowGIFModal();
    MBAnalytics.track('Chat::Input::PlusButton::ShowGIFModal', { draftId });
  };

  const handleSelectAttachment = () => {
    onSelectAttachment('all');
    MBAnalytics.track('Chat::Input::PlusButton::SelectAttachment', { draftId });
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      MBAnalytics.track('Chat::Input::PlusButton::Show', { draftId });
    }
  };

  return (
    <Menu onOpenChange={onOpenChange}>
      <PlusMenuButton />
      <Menu.Content>
        <Can I="create" a="Card" ability={channelAbility}>
          <Menu.Item
            icon="card"
            label={i18n('chat.input.plusButton.createCard')}
            onClick={onClickCreate}
          />
        </Can>
        <Menu.Item
          icon="imagestack"
          label={i18n('chat.input.plusButton.addImageOrFile')}
          onClick={handleSelectAttachment}
        />
        <Menu.Item
          icon="gif"
          label={i18n('chat.input.plusButton.gifSearch')}
          onClick={handleShowGIFModal}
        />

        {!hideUrgent && !urgent && (
          <Menu.Item
            icon="clock"
            label={i18n('chat.input.plusButton.sendAsUrgent')}
            onClick={handleSendAsUrgent}
          />
        )}

        {!hideUrgent && !!urgent && (
          <Menu.Item
            icon="clock"
            label={i18n('chat.input.plusButton.removeSendAsUrgent')}
            onClick={handleRemoveSendAsUrgent}
            danger
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

function PlusMenuButton() {
  const { isOpen } = useMenuContext();
  return (
    <Menu.Button
      variant={isOpen ? 'primary' : 'secondary'}
      size="medium"
      iconOnly
      icon="plus"
      iconSize={grid(3)}
      className={styles.plusButton}
    />
  );
}
